import React, { useState, useEffect } from 'react';
import { Settings, Eye, EyeOff, CheckSquare, MessageSquare, Clock, User, Bell, Mail, Save, AlertCircle, CheckCircle, X, ExternalLink, Plus, Trash2, Edit3, Globe, Layers, Info, Tag, ChevronDown, ChevronUp } from 'lucide-react';
import api from '../utils/api';


const ClientPortalSettings = ({ user, currentOrganization }) => {
  const [settings, setSettings] = useState({
    // Task visibility settings
    show_task_description: true,
    show_task_due_date: true,
    show_task_attachments: true,
    show_task_comments: true,
    show_task_progress: true,
    show_task_assignee: false,
    
    // Review workflow settings (Portal Stage Names)
    client_review_stage_name: "In Review",
    approved_stage_name: "Complete",
    revision_stage_name: "In Progress",
    
    // Review permissions
    allow_client_approval: true,
    allow_client_revisions: true,
    require_revision_comments: true,
    allow_bulk_actions: true,
    
    // Notification settings
    notify_team_on_approval: true,
    notify_team_on_revision: true,
    send_email_notifications: true,
    
    // Request form configuration
    enable_request_form: true,
    form_title: "Submit a Request",
    form_description: "Use this form to submit new requests or feedback to our team.",
    
    // Request Types (separate from other form fields)
    request_types: ["Bug Report", "Feature Request", "General Inquiry"],
    
    form_fields: [
      { id: 1, name: "title", label: "Title", type: "text", required: true },
      { id: 2, name: "description", label: "Description", type: "textarea", required: true },
      { id: 3, name: "attachments", label: "Attachments", type: "file", required: false }
    ],
    request_type_mappings: {},
    request_type_form_configs: {},
    request_form_schema_version: 1,
    
    // Stage mappings for portal views
    stage_mappings: {
      in_progress_stages: [],
      in_review_stages: [],
      complete_stages: []
    },
    
    // Action stage configuration
    revision_stage_id: null // Stage to move task to when revisions are requested
  });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [notification, setNotification] = useState({ show: false, type: '', message: '' });
  const [projectCategories, setProjectCategories] = useState([]);
  const [taskTemplates, setTaskTemplates] = useState([]);
  const [projectStages, setProjectStages] = useState([]);
  const [clientFieldDefinitions, setClientFieldDefinitions] = useState([]);
  const [referenceTables, setReferenceTables] = useState([]);
  const [selectedRequestTypeConfig, setSelectedRequestTypeConfig] = useState('');
  const [showAdvancedFieldKeys, setShowAdvancedFieldKeys] = useState(false);
  const [builderCollapsed, setBuilderCollapsed] = useState({
    instructions: false,
    baseFields: false,
    conditionalRules: false,
  });
  const [collapsedRules, setCollapsedRules] = useState({});
  
  // Collapseable sections state
  const [expandedSections, setExpandedSections] = useState({
    visibility: true,
    review: true,
    notifications: true,
    templates: true,
    stageMapping: true
  });

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  useEffect(() => {
    fetchSettings();
    fetchTemplates();
    fetchProjectStages();
    fetchClientFieldDefinitions();
    fetchReferenceTables();
  }, []);

  useEffect(() => {
    const types = settings.request_types || [];
    if (!selectedRequestTypeConfig && types.length > 0) {
      setSelectedRequestTypeConfig(types[0]);
    } else if (selectedRequestTypeConfig && !types.includes(selectedRequestTypeConfig)) {
      setSelectedRequestTypeConfig(types[0] || '');
    }
  }, [settings.request_types, selectedRequestTypeConfig]);

  const fetchSettings = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await api.get(`/portal/settings`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      const data = response.data;
      
      // Parse request_type_mappings if it's a string
      if (typeof data.request_type_mappings === 'string') {
        try {
          data.request_type_mappings = JSON.parse(data.request_type_mappings);
        } catch (e) {
          console.warn('Failed to parse request_type_mappings:', e);
          data.request_type_mappings = {};
        }
      }
      
      // Parse request_type_form_configs if it's a string
      if (typeof data.request_type_form_configs === 'string') {
        try {
          data.request_type_form_configs = JSON.parse(data.request_type_form_configs);
        } catch (e) {
          console.warn('Failed to parse request_type_form_configs:', e);
          data.request_type_form_configs = {};
        }
      }

      if (!data.request_type_form_configs || typeof data.request_type_form_configs !== 'object') {
        data.request_type_form_configs = {};
      }

      if (!data.request_form_schema_version) {
        data.request_form_schema_version = 1;
      }

      // Normalize request type names and align config keys to prevent disappearing configs after refresh
      if (Array.isArray(data.request_types)) {
        const normalized = [];
        const seen = new Set();
        data.request_types.forEach((rt) => {
          if (typeof rt !== 'string') return;
          const n = rt.trim();
          if (!n || seen.has(n)) return;
          seen.add(n);
          normalized.push(n);
        });
        data.request_types = normalized;
      }
      const cfg = data.request_type_form_configs || {};
      const aligned = {};
      const byNorm = Object.fromEntries((data.request_types || []).map(rt => [rt.trim(), rt]));
      Object.entries(cfg).forEach(([k, v]) => {
        const nk = (k || '').trim();
        const target = byNorm[nk] || nk;
        aligned[target] = v;
      });
      data.request_type_form_configs = aligned;

      // Parse stage_mappings if it's a string
      if (typeof data.stage_mappings === 'string') {
        try {
          data.stage_mappings = JSON.parse(data.stage_mappings);
        } catch (e) {
          console.warn('Failed to parse stage_mappings:', e);
          data.stage_mappings = {
            in_progress_stages: [],
            in_review_stages: [],
            complete_stages: []
          };
        }
      }
      
      // Ensure stage_mappings has default structure
      if (!data.stage_mappings) {
        data.stage_mappings = {
          in_progress_stages: [],
          in_review_stages: [],
          complete_stages: []
        };
      }
      
      // Migrate old format: extract request_type field options to request_types array
      if (!data.request_types || data.request_types.length === 0) {
        const requestTypeField = data.form_fields?.find(f => f.name === 'request_type' || f.name === 'Request Type');
        if (requestTypeField && requestTypeField.options) {
          data.request_types = requestTypeField.options;
          // Remove request_type from form_fields
          data.form_fields = data.form_fields.filter(f => f.name !== 'request_type' && f.name !== 'Request Type');
        } else {
          // Default request types if none exist
          data.request_types = ['Bug Report', 'Feature Request', 'General Inquiry'];
        }
      }
      
      setSettings(data);
    } catch (error) {
      console.error('Failed to fetch client portal settings:', error);
      showNotification('error', 'Failed to load client portal settings');
    } finally {
      setLoading(false);
    }
  };

  const fetchTemplates = async () => {
    try {
      const token = localStorage.getItem('access_token');
      
      // Fetch project categories
      const categoriesResponse = await api.get(`/project-categories`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setProjectCategories(categoriesResponse.data || []);
      
      // Fetch task templates
      const tasksResponse = await api.get(`/templates/tasks`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setTaskTemplates(tasksResponse.data || []);
    } catch (error) {
      console.error('Failed to fetch templates:', error);
    }
  };

  const fetchProjectStages = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await api.get(`/settings/default-stages`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setProjectStages(response.data || []);
    } catch (error) {
      console.error('Failed to fetch project stages:', error);
    }
  };

  const fetchClientFieldDefinitions = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await api.get(`/custom-field-definitions?entity_type=client`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setClientFieldDefinitions(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Failed to fetch client custom field definitions:', error);
      setClientFieldDefinitions([]);
    }
  };

  const fetchReferenceTables = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const teamId = currentOrganization?.id || user?.team_id || user?.teamId;
      if (!teamId) {
        setReferenceTables([]);
        return;
      }
      const response = await api.get(`/agent/reference-tables/${teamId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setReferenceTables(Array.isArray(response.data) ? response.data : (Array.isArray(response.data?.tables) ? response.data.tables : []));
    } catch (error) {
      console.error('Failed to fetch reference tables:', error);
      setReferenceTables([]);
    }
  };

  const showNotification = (type, message) => {
    setNotification({ show: true, type, message });
    setTimeout(() => setNotification({ show: false, type: '', message: '' }), 5000);
  };

  const handleInputChange = (field, value) => {
    setSettings(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleFormFieldChange = (fieldId, property, value) => {
    setSettings(prev => ({
      ...prev,
      form_fields: prev.form_fields.map(field => 
        field.id === fieldId ? { ...field, [property]: value } : field
      )
    }));
  };

  const addFormField = () => {
    const newField = {
      id: Date.now(),
      name: "",
      label: "",
      type: "text",
      options: [],
      required: false
    };
    
    setSettings(prev => ({
      ...prev,
      form_fields: [...(prev.form_fields || []), newField]
    }));
  };

  const removeFormField = (fieldId) => {
    setSettings(prev => ({
      ...prev,
      form_fields: (prev.form_fields || []).filter(field => field.id !== fieldId)
    }));
  };

  const handleOptionChange = (fieldId, optionIndex, value) => {
    setSettings(prev => ({
      ...prev,
      form_fields: prev.form_fields.map(field => 
        field.id === fieldId 
          ? { 
              ...field, 
              options: (field.options || []).map((opt, idx) => idx === optionIndex ? value : opt)
            }
          : field
      )
    }));
  };

  const addOption = (fieldId) => {
    setSettings(prev => ({
      ...prev,
      form_fields: prev.form_fields.map(field => 
        field.id === fieldId 
          ? { ...field, options: [...(field.options || []), ""] }
          : field
      )
    }));
  };

  const removeOption = (fieldId, optionIndex) => {
    setSettings(prev => ({
      ...prev,
      form_fields: prev.form_fields.map(field => 
        field.id === fieldId 
          ? { ...field, options: (field.options || []).filter((_, idx) => idx !== optionIndex) }
          : field
      )
    }));
  };

  const getRequestTypeConfig = (requestType) => {
    const cfgMap = settings.request_type_form_configs || {};
    if (!requestType) {
      return { version: settings.request_form_schema_version || 1, base_fields: [], conditional_rules: [] };
    }

    // Direct hit first
    if (cfgMap[requestType]) return cfgMap[requestType];

    // Robust fallback for key drift (trim/case/spacing)
    const target = String(requestType).trim().toLowerCase();
    const foundKey = Object.keys(cfgMap).find((k) => String(k).trim().toLowerCase() === target);
    if (foundKey && cfgMap[foundKey]) return cfgMap[foundKey];

    return { version: settings.request_form_schema_version || 1, base_fields: [], conditional_rules: [] };
  };

  const updateRequestTypeConfig = (requestType, nextConfig) => {
    setSettings(prev => ({
      ...prev,
      request_type_form_configs: {
        ...(prev.request_type_form_configs || {}),
        [requestType]: nextConfig
      }
    }));
  };

  const addRequestTypeBaseField = (requestType) => {
    const cfg = getRequestTypeConfig(requestType);
    const next = {
      ...cfg,
      base_fields: [
        ...(cfg.base_fields || []),
        { id: `field_${Date.now()}`, type: 'text', label: '', placeholder: '', required: false }
      ]
    };
    updateRequestTypeConfig(requestType, next);
  };

  const updateRequestTypeBaseField = (requestType, fieldId, key, value) => {
    const cfg = getRequestTypeConfig(requestType);
    const next = {
      ...cfg,
      base_fields: (cfg.base_fields || []).map(f =>
        (f.id === fieldId || f.name === fieldId) ? { ...f, [key]: value } : f
      )
    };
    updateRequestTypeConfig(requestType, next);
  };

  const removeRequestTypeBaseField = (requestType, fieldId) => {
    const cfg = getRequestTypeConfig(requestType);
    const next = {
      ...cfg,
      base_fields: (cfg.base_fields || []).filter(f => (f.id !== fieldId && f.name !== fieldId))
    };
    updateRequestTypeConfig(requestType, next);
  };

  const moveRequestTypeBaseField = (requestType, fieldIndex, direction) => {
    const cfg = getRequestTypeConfig(requestType);
    const arr = [...(cfg.base_fields || [])];
    const target = direction === 'up' ? fieldIndex - 1 : fieldIndex + 1;
    if (target < 0 || target >= arr.length) return;
    [arr[fieldIndex], arr[target]] = [arr[target], arr[fieldIndex]];
    updateRequestTypeConfig(requestType, { ...cfg, base_fields: arr });
  };

  const addRequestTypeRule = (requestType) => {
    const cfg = getRequestTypeConfig(requestType);
    const next = {
      ...cfg,
      conditional_rules: [
        ...(cfg.conditional_rules || []),
        {
          id: `rule_${Date.now()}`,
          when: { field: '', op: 'equals', value: '' },
          show_fields: [],
          conditional_rules: []
        }
      ]
    };
    updateRequestTypeConfig(requestType, next);
  };

  const addOptionalSubtypeTemplate = (requestType) => {
    const cfg = getRequestTypeConfig(requestType);
    const baseFields = [...(cfg.base_fields || [])];
    const hasSubtype = baseFields.some(f => (f?.id || f?.name) === 'request_subtype');

    if (!hasSubtype) {
      baseFields.unshift({
        id: 'request_subtype',
        label: 'Request Subtype',
        type: 'select',
        required: false,
        options: []
      });
    }

    updateRequestTypeConfig(requestType, {
      ...cfg,
      subtype_field: 'request_subtype',
      base_fields: baseFields
    });
  };

  const updateRequestTypeRule = (requestType, ruleId, updater) => {
    const cfg = getRequestTypeConfig(requestType);
    const next = {
      ...cfg,
      conditional_rules: (cfg.conditional_rules || []).map(r =>
        r.id === ruleId ? updater(r) : r
      )
    };
    updateRequestTypeConfig(requestType, next);
  };

  const removeRequestTypeRule = (requestType, ruleId) => {
    const cfg = getRequestTypeConfig(requestType);
    const next = {
      ...cfg,
      conditional_rules: (cfg.conditional_rules || []).filter(r => r.id !== ruleId)
    };
    updateRequestTypeConfig(requestType, next);
  };

  const moveRequestTypeRule = (requestType, ruleIndex, direction) => {
    const cfg = getRequestTypeConfig(requestType);
    const arr = [...(cfg.conditional_rules || [])];
    const target = direction === 'up' ? ruleIndex - 1 : ruleIndex + 1;
    if (target < 0 || target >= arr.length) return;
    [arr[ruleIndex], arr[target]] = [arr[target], arr[ruleIndex]];
    updateRequestTypeConfig(requestType, { ...cfg, conditional_rules: arr });
  };

  const removeRuleShowField = (requestType, ruleId, fieldIndex) => {
    updateRequestTypeRule(requestType, ruleId, (rule) => ({
      ...rule,
      show_fields: (rule.show_fields || []).filter((_, idx) => idx !== fieldIndex)
    }));
  };

  const moveRuleShowField = (requestType, ruleId, fieldIndex, direction) => {
    updateRequestTypeRule(requestType, ruleId, (rule) => {
      const arr = [...(rule.show_fields || [])];
      const target = direction === 'up' ? fieldIndex - 1 : fieldIndex + 1;
      if (target < 0 || target >= arr.length) return rule;
      [arr[fieldIndex], arr[target]] = [arr[target], arr[fieldIndex]];
      return { ...rule, show_fields: arr };
    });
  };

  const addRuleShowField = (requestType, ruleId) => {
    updateRequestTypeRule(requestType, ruleId, (rule) => ({
      ...rule,
      show_fields: [
        ...(rule.show_fields || []),
        { id: `field_${Date.now()}`, type: 'text', label: '', placeholder: '', required: false }
      ]
    }));
  };

  const addBaseFieldOption = (requestType, fieldId) => {
    const nextValue = (window.prompt('Add option value:') || '').trim();
    if (!nextValue) return;
    const cfg = getRequestTypeConfig(requestType);
    const next = {
      ...cfg,
      base_fields: (cfg.base_fields || []).map(f =>
        (f.id === fieldId || f.name === fieldId)
          ? { ...f, options: [...(f.options || []), nextValue] }
          : f
      )
    };
    updateRequestTypeConfig(requestType, next);
  };

  const removeBaseFieldOption = (requestType, fieldId, optionIndex) => {
    const cfg = getRequestTypeConfig(requestType);
    const next = {
      ...cfg,
      base_fields: (cfg.base_fields || []).map(f =>
        (f.id === fieldId || f.name === fieldId)
          ? { ...f, options: (f.options || []).filter((_, idx) => idx !== optionIndex) }
          : f
      )
    };
    updateRequestTypeConfig(requestType, next);
  };

  const addRuleFieldOption = (requestType, ruleId, fieldIndex) => {
    const nextValue = (window.prompt('Add option value:') || '').trim();
    if (!nextValue) return;
    updateRequestTypeRule(requestType, ruleId, (r) => ({
      ...r,
      show_fields: (r.show_fields || []).map((sf, sfi) =>
        sfi === fieldIndex ? { ...sf, options: [...(sf.options || []), nextValue] } : sf
      )
    }));
  };

  const removeRuleFieldOption = (requestType, ruleId, fieldIndex, optionIndex) => {
    updateRequestTypeRule(requestType, ruleId, (r) => ({
      ...r,
      show_fields: (r.show_fields || []).map((sf, sfi) =>
        sfi === fieldIndex ? { ...sf, options: (sf.options || []).filter((_, idx) => idx !== optionIndex) } : sf
      )
    }));
  };

  const getResolvedSelectOptions = (field) => {
    if (!field || (field.type || 'text') !== 'select') return [];

    const source = field.options_source || 'static';
    if (source === 'client_custom_field') {
      const ref = String(field.options_custom_field_id || field.options_custom_field_name || '').trim();
      if (!ref) return [];
      const def = clientFieldDefinitions.find(d => String(d.id) === ref || d.name === ref);
      const opts = def?.options;
      if (Array.isArray(opts)) return opts.map(v => String(v).trim()).filter(Boolean);
      return [];
    }

    return Array.isArray(field.options) ? field.options.map(v => String(v).trim()).filter(Boolean) : [];
  };

  const getConditionFieldCandidates = (requestType) => {
    const cfg = getRequestTypeConfig(requestType);
    const fields = [];

    // Core fields always available for conditions
    fields.push({ id: 'request_type', label: 'Request Type', type: 'select', options: settings.request_types || [], source: 'core' });
    fields.push({ id: 'title', label: 'Title', type: 'text', options: [], source: 'core' });
    fields.push({ id: 'description', label: 'Description', type: 'textarea', options: [], source: 'core' });

    // Always-on form fields (global form config)
    (settings.form_fields || []).forEach(f => {
      const id = f?.id || f?.name;
      if (id) {
        fields.push({
          id: String(id),
          label: f?.label || String(id),
          type: f?.type || 'text',
          options: Array.isArray(f?.options) ? f.options.map(v => String(v).trim()).filter(Boolean) : [],
          source: 'always_on'
        });
      }
    });

    // Request-type form fields
    (cfg.base_fields || []).forEach(f => {
      const id = f?.id || f?.name;
      if (id) fields.push({ id, label: f?.label || id, type: f?.type || 'text', options: getResolvedSelectOptions(f), source: 'form' });
    });

    (cfg.conditional_rules || []).forEach(rule => {
      (rule.show_fields || []).forEach(f => {
        const id = f?.id || f?.name;
        if (id) fields.push({ id, label: f?.label || id, type: f?.type || 'text', options: getResolvedSelectOptions(f), source: 'form' });
      });
    });

    // Client custom fields (can be used as rule conditions directly)
    (clientFieldDefinitions || []).forEach(def => {
      const ref = `client_custom.${def.name || def.id}`;
      const options = Array.isArray(def.options)
        ? def.options.map(v => String(v).trim()).filter(Boolean)
        : [];
      fields.push({
        id: ref,
        label: `Client Field: ${def.label || def.name || def.id}`,
        type: def.type || 'text',
        options,
        source: 'client_custom'
      });
    });

    const seen = new Set();
    return fields.filter(f => {
      if (seen.has(f.id)) return false;
      seen.add(f.id);
      return true;
    });
  };

  const openClientPortalPreview = async () => {
    console.log('🚀 Opening client portal preview...');
    
    try {
      const token = localStorage.getItem('access_token');
      
      if (!token) {
        console.error('❌ No access token found!');
        showNotification('error', 'Please log in to preview the portal');
        return;
      }
      
      // Call the admin portal preview endpoint
      const response = await api.get('/admin/portal/preview', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      console.log('✅ Portal preview response:', response.data);
      
      if (response.data.success) {
        // Pass token via URL hash (same as ClientSettings.js)
        const portalData = {
          token: response.data.token,
          team_id: response.data.team_id,
          contact: response.data.contact,
          client: response.data.client,
          is_preview: true
        };
        
        // Encode data as base64 to pass in URL hash
        const encodedData = btoa(JSON.stringify(portalData));
        const portalUrl = `${window.location.origin}/portal/${response.data.team_id}/dashboard#auth=${encodedData}`;
        
        console.log('🔗 Opening portal preview URL:', portalUrl);
        console.log('🔗 Encoded data length:', encodedData.length);
        
        // Open in new tab - use simpler window.open without restrictive options
        // The 'noopener,noreferrer' can sometimes cause issues with hash preservation
        const newWindow = window.open(portalUrl, '_blank');
        if (!newWindow) {
          showNotification('error', 'Pop-up blocked. Please allow pop-ups for this site.');
        }
      } else {
        // Show error message
        const errorMsg = response.data.error || 'Unable to preview portal. Please check portal settings.';
        showNotification('error', errorMsg);
      }
    } catch (error) {
      console.error('❌ Error opening portal preview:', error);
      
      let errorMessage = 'Failed to open portal preview. Please try again.';
      
      if (error.response?.data) {
        if (typeof error.response.data.error === 'string') {
          errorMessage = error.response.data.error;
        } else if (typeof error.response.data.detail === 'string') {
          errorMessage = error.response.data.detail;
        }
      }
      
      showNotification('error', errorMessage);
    }
  };

  const generatePortalUrl = (clientId, useCustomDomain = false, customDomain = '') => {
    if (useCustomDomain && customDomain) {
      return `${customDomain}/portal/${currentOrganization?.id || user?.team_id || 'demo-tenant'}`;
    }
    
    const tenantId = user?.team_id || currentOrganization?.id || 'demo-tenant';
    return `${window.location.origin}/portal/${tenantId}`;
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('access_token');
      
      // DEBUG: Log what we're about to save
      console.log('🔍 SAVE DEBUG - Current settings state:', settings);
      console.log('🔍 SAVE DEBUG - Form fields being saved:', settings.form_fields);
      console.log('🔍 SAVE DEBUG - Number of form fields:', settings.form_fields?.length || 0);
      console.log('🔍 SAVE DEBUG - Form fields JSON:', JSON.stringify(settings.form_fields, null, 2));
      
      // Validate required fields
      if (!settings.client_review_stage_name?.trim()) {
        showNotification('error', 'Client review stage name is required');
        setSaving(false);
        return;
      }

      if (!settings.approved_stage_name?.trim()) {
        showNotification('error', 'Approved stage name is required');
        setSaving(false);
        return;
      }

      if (!settings.revision_stage_name?.trim()) {
        showNotification('error', 'Revision stage name is required');
        setSaving(false);
        return;
      }

      // Normalize request types and prevent accidental empty saves
      const normalizedRequestTypes = Array.from(new Set(
        (settings.request_types || [])
          .map(t => (typeof t === 'string' ? t.trim() : ''))
          .filter(Boolean)
      ));

      if (settings.enable_request_form && normalizedRequestTypes.length === 0) {
        showNotification('error', 'At least one request type is required.');
        setSaving(false);
        return;
      }

      const configByNorm = {};
      Object.entries(settings.request_type_form_configs || {}).forEach(([k, v]) => {
        const nk = (k || '').trim();
        if (!nk) return;
        configByNorm[nk] = v;
      });
      const alignedConfigs = {};
      normalizedRequestTypes.forEach((rt) => {
        const nk = rt.trim();
        if (configByNorm[nk] !== undefined) alignedConfigs[rt] = configByNorm[nk];
      });

      const payload = {
        ...settings,
        request_types: normalizedRequestTypes,
        request_type_form_configs: alignedConfigs
      };

      console.log('🚀 SAVE DEBUG - Sending to backend:', payload);

      const response = await api.put(`/portal/settings`, payload, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      console.log('✅ SAVE DEBUG - Backend response:', response.data);
      showNotification('success', 'Client portal settings saved successfully');
    } catch (error) {
      console.error('❌ SAVE DEBUG - Save failed:', error);
      console.error('❌ SAVE DEBUG - Error response:', error.response?.data);
      showNotification('error', 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const resetToDefaults = () => {
    setSettings({
      // Task visibility settings
      show_task_description: true,
      show_task_due_date: true,
      show_task_attachments: true,
      show_task_comments: true,
      show_task_progress: true,
      show_task_assignee: false,
      
      // Review workflow settings
      client_review_stage_name: "Client Review",
      approved_stage_name: "Done",
      revision_stage_name: "In Progress",
      
      // Review permissions
      allow_client_approval: true,
      allow_client_revisions: true,
      require_revision_comments: true,
      allow_bulk_actions: true,
      
      // Notification settings
      notify_team_on_approval: true,
      notify_team_on_revision: true,
      send_email_notifications: true,
      
      // Request form configuration
      enable_request_form: true,
      form_title: "Submit a Request",
      form_description: "Use this form to submit new requests or feedback to our team.",
      form_fields: [
        { id: 1, name: "title", label: "Title", type: "text", required: true },
        { id: 2, name: "description", label: "Description", type: "textarea", required: true },
        { id: 3, name: "attachments", label: "Attachments", type: "file", required: false }
      ]
    });
  };

  if (loading) {
    return (
      <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 mb-8">
        <div className="animate-pulse space-y-4">
          <div className="h-6 bg-gray-200 rounded w-1/3"></div>
          <div className="space-y-3">
            <div className="h-10 bg-gray-200 rounded"></div>
            <div className="h-10 bg-gray-200 rounded"></div>
            <div className="h-20 bg-gray-200 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Notification */}
      {notification.show && (
        <div className={`fixed top-4 right-4 z-50 flex items-center space-x-2 px-4 py-3 rounded-lg shadow-lg ${
          notification.type === 'success' 
            ? 'bg-green-100 border border-green-200 text-green-800'
            : 'bg-red-100 border border-red-200 text-red-800'
        }`}>
          {notification.type === 'success' ? (
            <CheckCircle className="w-5 h-5" />
          ) : (
            <AlertCircle className="w-5 h-5" />
          )}
          <span className="text-sm font-medium">{notification.message}</span>
          <button
            onClick={() => setNotification({ show: false, type: '', message: '' })}
            className="text-current opacity-70 hover:opacity-100"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 mb-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="flex items-center space-x-2 mb-2">
              <h2 className="text-xl font-semibold text-gray-900">Client Portal Settings</h2>
              <span className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full">Admin Only</span>
            </div>
            <p className="text-gray-600">Configure what clients can see and do in their portal</p>
          </div>
          
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center space-x-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
          >
            {saving ? (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
            ) : (
              <Save className="w-4 h-4" />
            )}
            <span>{saving ? 'Saving...' : 'Save Changes'}</span>
          </button>
        </div>

        <div className="space-y-8">
          {/* Message Display */}
          {message.text && (
            <div className={`mb-6 p-4 rounded-lg flex items-center space-x-2 ${
              message.type === 'success' 
                ? 'bg-green-100 text-green-800 border border-green-200' 
                : 'bg-red-100 text-red-800 border border-red-200'
            }`}>
              {message.type === 'success' ? (
                <CheckCircle className="w-5 h-5" />
              ) : (
                <AlertCircle className="w-5 h-5" />
              )}
              <span>{message.text}</span>
            </div>
          )}
          
          {/* Enable Portal Toggle - Organization Level */}
          <div className="bg-white border-2 border-gray-200 rounded-lg p-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-1">Enable Client Portal</h3>
                <p className="text-sm text-gray-600">
                  Master switch to enable or disable the client portal for your entire organization. Individual clients can then be enabled separately.
                </p>
              </div>
              <button
                onClick={() => setSettings(prev => ({ ...prev, portal_enabled: !prev.portal_enabled }))}
                className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                  settings.portal_enabled ? 'bg-blue-600' : 'bg-gray-200'
                }`}
              >
                <span
                  className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                    settings.portal_enabled ? 'translate-x-5' : 'translate-x-0'
                  }`}
                />
              </button>
            </div>
            {!settings.portal_enabled && (
              <div className="mt-3 flex items-start space-x-2 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-yellow-800">
                  Portal is currently disabled organization-wide. Enable it here first, then configure individual client access in their settings.
                </p>
              </div>
            )}
          </div>
          
          {/* Portal Preview - Compact Info Box */}
          <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg p-6 border border-blue-200">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <h3 className="text-lg font-medium text-gray-900 mb-3 flex items-center space-x-2">
                  <Globe className="w-5 h-5" />
                  <span>Portal Information</span>
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <h4 className="text-sm font-medium text-blue-900 mb-1">Default Portal</h4>
                    <p className="text-sm text-blue-800 mb-2">
                      <code className="bg-blue-100 px-2 py-1 rounded text-xs">
                        portal.projectathena.us/[tenant-id]/[client-id]
                      </code>
                    </p>
                    <p className="text-xs text-blue-700">
                      Automatic for all clients
                    </p>
                  </div>
                  
                  <div>
                    <h4 className="text-sm font-medium text-purple-900 mb-1">Custom Domain (Premium)</h4>
                    <p className="text-sm text-purple-800 mb-2">
                      Configure in <strong>White-Label Branding Settings</strong>
                    </p>
                    <p className="text-xs text-purple-700">
                      Use your own domain for white-label experience
                    </p>
                  </div>
                </div>
              </div>
              
              <button
                onClick={openClientPortalPreview}
                className="flex items-center space-x-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors text-sm ml-4"
              >
                <ExternalLink className="w-4 h-4" />
                <span>Preview Portal</span>
              </button>
            </div>
          </div>

          {/* Portal URL Share Section */}
          {settings.portal_enabled && currentOrganization?.id && (
            <div className="bg-gradient-to-r from-blue-50 to-purple-50 border-2 border-blue-200 rounded-lg p-6">
              <div className="flex items-start space-x-3">
                <Globe className="w-6 h-6 text-blue-600 flex-shrink-0 mt-1" />
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Your Client Portal Link</h3>
                  <p className="text-sm text-gray-600 mb-4">
                    Share this link with your clients to give them access to their portal. They can create an account and view their projects and tasks.
                  </p>
                  
                  <div className="bg-white rounded-lg p-4 border-2 border-blue-300">
                    <div className="flex items-center justify-between">
                      <code className="text-sm text-blue-700 font-mono break-all flex-1">
                        {window.location.origin}/portal/{currentOrganization.id}
                      </code>
                      <button
                        onClick={() => {
                          const url = `${window.location.origin}/portal/${currentOrganization.id}`;
                          navigator.clipboard.writeText(url);
                          setMessage({ type: 'success', text: '✅ Portal URL copied to clipboard!' });
                          setTimeout(() => setMessage({ type: '', text: '' }), 3000);
                        }}
                        className="ml-4 flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex-shrink-0"
                      >
                        <ExternalLink className="w-4 h-4" />
                        <span>Copy Link</span>
                      </button>
                    </div>
                  </div>
                  
                  <p className="text-xs text-gray-500 mt-3">
                    💡 Add this link to your website, support emails, or client communications
                  </p>
                </div>
              </div>
            </div>
          )}
          
          {/* Task Visibility Settings */}
          <div>
            <button
              type="button"
              onClick={() => toggleSection('visibility')}
              className="w-full flex items-center justify-between text-lg font-medium text-gray-900 mb-4 hover:text-blue-600 transition-colors"
            >
              <div className="flex items-center space-x-2">
                <Eye className="w-5 h-5" />
                <span>Task Visibility</span>
              </div>
              {expandedSections.visibility ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
            </button>
            
            {expandedSections.visibility && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[
                { key: 'show_task_description', label: 'Task Description', icon: MessageSquare },
                { key: 'show_task_due_date', label: 'Due Dates', icon: Clock },
                { key: 'show_task_attachments', label: 'File Attachments', icon: 'paperclip' },
                { key: 'show_task_comments', label: 'Comments & Activity', icon: MessageSquare },
                { key: 'show_task_progress', label: 'Progress Percentage', icon: 'progress' },
                { key: 'show_task_assignee', label: 'Assigned Team Member', icon: User }
              ].map(({ key, label, icon: Icon }) => (
                <label key={key} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer">
                  <div className="flex items-center space-x-3">
                    {typeof Icon === 'string' ? (
                      <div className="w-5 h-5 text-gray-400">
                        {Icon === 'paperclip' && '📎'}
                        {Icon === 'progress' && '📊'}
                      </div>
                    ) : (
                      <Icon className="w-5 h-5 text-gray-400" />
                    )}
                    <span className="font-medium text-gray-700">{label}</span>
                  </div>
                  
                  <div className="relative">
                    <input
                      type="checkbox"
                      checked={settings[key]}
                      onChange={(e) => handleInputChange(key, e.target.checked)}
                      className="sr-only"
                    />
                    <div className={`w-12 h-6 rounded-full transition-colors ${
                      settings[key] ? 'bg-blue-600' : 'bg-gray-200'
                    }`}>
                      <div className={`w-5 h-5 rounded-full bg-white shadow transform transition-transform ${
                        settings[key] ? 'translate-x-6' : 'translate-x-0.5'
                      }`} style={{ marginTop: '2px' }}></div>
                    </div>
                  </div>
                </label>
              ))}
            </div>
            )}
          </div>

          {/* Review Workflow Settings */}
          <div>
            <button
              type="button"
              onClick={() => toggleSection('review')}
              className="w-full flex items-center justify-between text-lg font-medium text-gray-900 mb-4 hover:text-blue-600 transition-colors"
            >
              <div className="flex items-center space-x-2">
                <CheckSquare className="w-5 h-5" />
                <span>Review Workflow</span>
              </div>
              {expandedSections.review ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
            </button>
            
            {expandedSections.review && (
            <>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  In Progress Stage (Portal Label)
                </label>
                <input
                  type="text"
                  value={settings.revision_stage_name}
                  onChange={(e) => handleInputChange('revision_stage_name', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="In Progress"
                />
                <p className="text-xs text-gray-500 mt-1">Tasks being worked on</p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  In Review Stage (Portal Label)
                </label>
                <input
                  type="text"
                  value={settings.client_review_stage_name}
                  onChange={(e) => handleInputChange('client_review_stage_name', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="In Review"
                />
                <p className="text-xs text-gray-500 mt-1">Awaiting client review</p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Complete Stage (Portal Label)
                </label>
                <input
                  type="text"
                  value={settings.approved_stage_name}
                  onChange={(e) => handleInputChange('approved_stage_name', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Complete"
                />
                <p className="text-xs text-gray-500 mt-1">Completed tasks</p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[
                { key: 'allow_client_approval', label: 'Allow Client Approval', icon: CheckCircle },
                { key: 'allow_client_revisions', label: 'Allow Revision Requests', icon: MessageSquare },
                { key: 'require_revision_comments', label: 'Require Revision Comments', icon: MessageSquare },
                { key: 'allow_bulk_actions', label: 'Allow Bulk Actions', icon: CheckSquare },
                { key: 'notify_team_on_approval', label: 'Notify Team on Approval', icon: Bell },
                { key: 'send_email_notifications', label: 'Send Email Notifications', icon: Mail }
              ].map(({ key, label, icon: Icon }) => (
                <label key={key} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer">
                  <div className="flex items-center space-x-3">
                    <Icon className="w-5 h-5 text-gray-400" />
                    <span className="font-medium text-gray-700">{label}</span>
                  </div>
                  
                  <div className="relative">
                    <input
                      type="checkbox"
                      checked={settings[key]}
                      onChange={(e) => handleInputChange(key, e.target.checked)}
                      className="sr-only"
                    />
                    <div className={`w-12 h-6 rounded-full transition-colors ${
                      settings[key] ? 'bg-indigo-600' : 'bg-gray-200'
                    }`}>
                      <div className={`w-5 h-5 rounded-full bg-white shadow transform transition-transform ${
                        settings[key] ? 'translate-x-6' : 'translate-x-0.5'
                      }`} style={{ marginTop: '2px' }}></div>
                    </div>
                  </div>
                </label>
              ))}
            </div>
            </>
            )}
          </div>

          {/* Portal Stage Mapping */}
          <div>
            <button
              type="button"
              onClick={() => toggleSection('stageMapping')}
              className="w-full flex items-center justify-between text-lg font-medium text-gray-900 mb-4 hover:text-blue-600 transition-colors"
            >
              <div className="flex items-center space-x-2">
                <Layers className="w-5 h-5" />
                <span>Portal Stage Mapping</span>
              </div>
              {expandedSections.stageMapping ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
            </button>
            
            {expandedSections.stageMapping && (
            <>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                <div className="flex items-start space-x-2">
                  <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-blue-800">
                    <p className="font-medium mb-2">Configure Stage Mapping</p>
                    <p className="mb-2">Map your internal project stages to the three portal stages that clients see. This ensures tasks appear in the correct portal stage regardless of your internal workflow.</p>
                    <ul className="list-disc list-inside space-y-1">
                      <li><strong>In Progress:</strong> Tasks actively being worked on</li>
                      <li><strong>In Review:</strong> Tasks ready for client review</li>
                      <li><strong>Complete:</strong> Finished and approved tasks</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {/* In Progress Stages */}
                <div className="border border-gray-200 rounded-lg p-4">
                  <h4 className="text-md font-semibold text-gray-900 mb-3 flex items-center">
                    <div className="w-3 h-3 rounded-full bg-slate-400 mr-2"></div>
                    In Progress
                  </h4>
                  <p className="text-xs text-gray-600 mb-3">Select stages that represent work in progress</p>
                  <div className="space-y-2">
                    {projectStages.map((stage) => (
                      <label key={stage.id} className="flex items-center space-x-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={(settings.stage_mappings?.in_progress_stages || []).includes(stage.id)}
                          onChange={(e) => {
                            const currentMappings = settings.stage_mappings || {
                              in_progress_stages: [],
                              in_review_stages: [],
                              complete_stages: []
                            };
                            
                            let updatedStages;
                            if (e.target.checked) {
                              updatedStages = [...(currentMappings.in_progress_stages || []), stage.id];
                            } else {
                              updatedStages = (currentMappings.in_progress_stages || []).filter(id => id !== stage.id);
                            }
                            
                            handleInputChange('stage_mappings', {
                              ...currentMappings,
                              in_progress_stages: updatedStages
                            });
                          }}
                          className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                        />
                        <div className="flex items-center space-x-2">
                          <div 
                            className="w-3 h-3 rounded-full" 
                            style={{ backgroundColor: stage.color || '#6B7280' }}
                          ></div>
                          <span className="text-sm text-gray-700">{stage.name}</span>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>

                {/* In Review Stages */}
                <div className="border border-gray-200 rounded-lg p-4">
                  <h4 className="text-md font-semibold text-gray-900 mb-3 flex items-center">
                    <div className="w-3 h-3 rounded-full bg-amber-500 mr-2"></div>
                    In Review
                  </h4>
                  <p className="text-xs text-gray-600 mb-3">Select stages for client review</p>
                  <div className="space-y-2">
                    {projectStages.map((stage) => (
                      <label key={stage.id} className="flex items-center space-x-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={(settings.stage_mappings?.in_review_stages || []).includes(stage.id)}
                          onChange={(e) => {
                            const currentMappings = settings.stage_mappings || {
                              in_progress_stages: [],
                              in_review_stages: [],
                              complete_stages: []
                            };
                            
                            let updatedStages;
                            if (e.target.checked) {
                              updatedStages = [...(currentMappings.in_review_stages || []), stage.id];
                            } else {
                              updatedStages = (currentMappings.in_review_stages || []).filter(id => id !== stage.id);
                            }
                            
                            handleInputChange('stage_mappings', {
                              ...currentMappings,
                              in_review_stages: updatedStages
                            });
                          }}
                          className="w-4 h-4 text-amber-600 border-gray-300 rounded focus:ring-amber-500"
                        />
                        <div className="flex items-center space-x-2">
                          <div 
                            className="w-3 h-3 rounded-full" 
                            style={{ backgroundColor: stage.color || '#6B7280' }}
                          ></div>
                          <span className="text-sm text-gray-700">{stage.name}</span>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Complete Stages */}
                <div className="border border-gray-200 rounded-lg p-4">
                  <h4 className="text-md font-semibold text-gray-900 mb-3 flex items-center">
                    <div className="w-3 h-3 rounded-full bg-green-500 mr-2"></div>
                    Complete
                  </h4>
                  <p className="text-xs text-gray-600 mb-3">Select completed/approved stages</p>
                  <div className="space-y-2">
                    {projectStages.map((stage) => (
                      <label key={stage.id} className="flex items-center space-x-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={(settings.stage_mappings?.complete_stages || []).includes(stage.id)}
                          onChange={(e) => {
                            const currentMappings = settings.stage_mappings || {
                              in_progress_stages: [],
                              in_review_stages: [],
                              complete_stages: []
                            };
                            
                            let updatedStages;
                            if (e.target.checked) {
                              updatedStages = [...(currentMappings.complete_stages || []), stage.id];
                            } else {
                              updatedStages = (currentMappings.complete_stages || []).filter(id => id !== stage.id);
                            }
                            
                            handleInputChange('stage_mappings', {
                              ...currentMappings,
                              complete_stages: updatedStages
                            });
                          }}
                          className="w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
                        />
                        <div className="flex items-center space-x-2">
                          <div 
                            className="w-3 h-3 rounded-full" 
                            style={{ backgroundColor: stage.color || '#6B7280' }}
                          ></div>
                          <span className="text-sm text-gray-700">{stage.name}</span>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            </>
            )}
          </div>
        </div>
        
        {/* Task Action Configuration */}
        <div className="mt-8 p-6 bg-gray-50 rounded-lg border border-purple-200">
          <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center space-x-2">
            <Layers className="w-5 h-5 text-purple-600" />
            <span>Task Action Configuration</span>
          </h3>
          <p className="text-sm text-gray-600 mb-4">
            Configure what happens when clients approve or request revisions on tasks.
          </p>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Revision Stage
                <span className="text-red-500 ml-1">*</span>
              </label>
              <select
                value={settings.revision_stage_id || ''}
                onChange={(e) => handleInputChange('revision_stage_id', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              >
                <option value="">Select a stage...</option>
                {projectStages.map((stage) => (
                  <option key={stage.id} value={stage.id}>
                    {stage.name}
                  </option>
                ))}
              </select>
              <p className="text-xs text-gray-500 mt-1">
                When clients request revisions, tasks will be moved to this stage
              </p>
            </div>
            
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-start gap-2">
                <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-blue-900">
                  <p className="font-medium mb-1">How Task Actions Work:</p>
                  <ul className="list-disc list-inside space-y-1 text-blue-800">
                    <li><strong>Approve:</strong> Moves task to the last stage in its project</li>
                    <li><strong>Request Revisions:</strong> Moves task to the stage selected above</li>
                    <li>Both actions notify assigned team members</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Request Form Configuration */}
        <div className="mt-8 p-6 bg-gray-50 rounded-lg">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-medium text-gray-900 flex items-center space-x-2">
              <MessageSquare className="w-5 h-5" />
              <span>Request Form Configuration</span>
            </h3>
            
            <label className="flex items-center space-x-2">
              <span className="text-sm text-gray-600">Enable Form</span>
              <div className="relative">
                <input
                  type="checkbox"
                  checked={settings.enable_request_form}
                  onChange={(e) => handleInputChange('enable_request_form', e.target.checked)}
                  className="sr-only"
                />
                <div className={`w-12 h-6 rounded-full transition-colors ${
                  settings.enable_request_form ? 'bg-green-600' : 'bg-gray-200'
                }`}>
                  <div className={`w-5 h-5 rounded-full bg-white shadow transform transition-transform ${
                    settings.enable_request_form ? 'translate-x-6' : 'translate-x-0.5'
                  }`} style={{ marginTop: '2px' }}></div>
                </div>
              </div>
            </label>
          </div>

          {settings.enable_request_form && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Form Title
                  </label>
                  <input
                    type="text"
                    value={settings.form_title}
                    onChange={(e) => handleInputChange('form_title', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Submit a Request"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Form Description
                  </label>
                  <textarea
                    value={settings.form_description}
                    onChange={(e) => handleInputChange('form_description', e.target.value)}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                    placeholder="Use this form to submit new requests..."
                  />
                </div>
              </div>

              {/* Request Types Management */}
              <div className="border-t border-gray-200 pt-6">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h4 className="text-md font-medium text-gray-900 flex items-center">
                      <Tag className="w-4 h-4 mr-2 text-purple-600" />
                      Request Types
                    </h4>
                    <p className="text-sm text-gray-600 mt-1">Define the types of requests clients can submit</p>
                  </div>
                  <button
                    onClick={() => {
                      const newTypeName = `New Request Type ${(settings.request_types || []).length + 1}`;
                      const newTypes = [...(settings.request_types || []), newTypeName];
                      setSettings(prev => ({
                        ...prev,
                        request_types: newTypes,
                        request_type_form_configs: {
                          ...(prev.request_type_form_configs || {}),
                          [newTypeName]: { version: prev.request_form_schema_version || 1, base_fields: [], conditional_rules: [] }
                        }
                      }));
                      setSelectedRequestTypeConfig(newTypeName);
                    }}
                    className="flex items-center space-x-2 px-3 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg hover:from-purple-700 hover:to-pink-700 transition-all text-sm shadow-md"
                  >
                    <Plus className="w-4 h-4" />
                    <span>Add Request Type</span>
                  </button>
                </div>

                <div className="space-y-2">
                  {(settings.request_types || []).map((requestType, index) => (
                    <div key={index} className="flex items-center space-x-2 bg-gray-50 rounded-lg p-3 border border-gray-200">
                      <input
                        type="text"
                        value={requestType}
                        onChange={(e) => {
                          const newName = e.target.value;
                          const newTypes = [...settings.request_types];
                          newTypes[index] = newName;
                          const nextConfigs = { ...(settings.request_type_form_configs || {}) };
                          if (newName && newName !== requestType && nextConfigs[requestType]) {
                            nextConfigs[newName] = nextConfigs[requestType];
                            delete nextConfigs[requestType];
                          }
                          setSettings(prev => ({
                            ...prev,
                            request_types: newTypes,
                            request_type_form_configs: nextConfigs
                          }));
                        }}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                        placeholder="Request Type Name"
                      />
                      <button
                        onClick={() => {
                          const newTypes = settings.request_types.filter((_, i) => i !== index);
                          const newMappings = { ...(settings.request_type_mappings || {}) };
                          delete newMappings[requestType];
                          const newConfigs = { ...(settings.request_type_form_configs || {}) };
                          delete newConfigs[requestType];
                          setSettings(prev => ({
                            ...prev,
                            request_types: newTypes,
                            request_type_mappings: newMappings,
                            request_type_form_configs: newConfigs
                          }));
                        }}
                        className="text-red-600 hover:text-red-800 transition-colors p-2"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}

                  {(!settings.request_types || settings.request_types.length === 0) && (
                    <div className="text-center py-6 text-gray-500 bg-gray-50 rounded-lg border border-gray-200">
                      <p>No request types defined. Click "Add Request Type" to create one.</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Form Fields Configuration */}
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h4 className="text-md font-medium text-gray-900">Always-On Form Fields (All Request Types)</h4>
                  <button
                    onClick={addFormField}
                    className="flex items-center space-x-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
                  >
                    <Plus className="w-4 h-4" />
                    <span>Add Field</span>
                  </button>
                </div>

                <div className="space-y-4">
                  {(settings.form_fields || []).map((field, index) => (
                    <div key={field.id} className="border border-gray-200 rounded-lg p-4 bg-white">
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-sm font-medium text-gray-600">Field #{index + 1}</span>
                        <button
                          onClick={() => removeFormField(field.id)}
                          className="text-red-600 hover:text-red-800 transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">Field Name</label>
                          <input
                            type="text"
                            value={field.name || ''}
                            onChange={(e) => handleFormFieldChange(field.id, 'name', e.target.value)}
                            className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-transparent"
                            placeholder="field_name"
                          />
                        </div>
                        
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">Label</label>
                          <input
                            type="text"
                            value={field.label || ''}
                            onChange={(e) => handleFormFieldChange(field.id, 'label', e.target.value)}
                            className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-transparent"
                            placeholder="Field Label"
                          />
                        </div>
                        
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">Type</label>
                          <select
                            value={field.type || 'text'}
                            onChange={(e) => handleFormFieldChange(field.id, 'type', e.target.value)}
                            className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-transparent"
                          >
                            <option value="text">Text</option>
                            <option value="textarea">Textarea</option>
                            <option value="select">Select</option>
                            <option value="email">Email</option>
                            <option value="file">File Upload</option>
                            <option value="number">Number</option>
                          </select>
                        </div>
                      </div>

                      {/* Options for Select fields */}
                      {field.type === 'select' && (
                        <div className="mb-3">
                          <label className="block text-xs font-medium text-gray-600 mb-2">Options</label>
                          <div className="space-y-2">
                            {(field.options || []).map((option, optIndex) => (
                              <div key={optIndex} className="flex items-center space-x-2">
                                <input
                                  type="text"
                                  value={option || ''}
                                  onChange={(e) => handleOptionChange(field.id, optIndex, e.target.value)}
                                  className="flex-1 px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-transparent"
                                  placeholder={`Option ${optIndex + 1}`}
                                />
                                <button
                                  onClick={() => removeOption(field.id, optIndex)}
                                  className="text-red-600 hover:text-red-800 transition-colors"
                                >
                                  <X className="w-4 h-4" />
                                </button>
                              </div>
                            ))}
                            <button
                              onClick={() => addOption(field.id)}
                              className="flex items-center space-x-1 px-2 py-1 text-xs text-blue-600 hover:text-blue-800 transition-colors"
                            >
                              <Plus className="w-3 h-3" />
                              <span>Add Option</span>
                            </button>
                          </div>
                        </div>
                      )}

                      <label className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          checked={field.required || false}
                          onChange={(e) => handleFormFieldChange(field.id, 'required', e.target.checked)}
                          className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                        />
                        <span className="text-xs text-gray-600">Required field</span>
                      </label>
                    </div>
                  ))}
                </div>
              </div>
              {/* Conditional Form Variants (Phase 2) */}
              <div className="border-t border-gray-200 pt-6">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h4 className="text-md font-medium text-gray-900 flex items-center">
                      <Layers className="w-4 h-4 mr-2 text-indigo-600" />
                      Conditional Form Variants by Request Type
                    </h4>
                    <p className="text-sm text-gray-600 mt-1">
                      Configure extra fields by request type. Core fields (request type, title, description, attachments) remain available across all request types.
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      Tip: Use <strong>Add Optional Subtype</strong> for request types like Website Request/Bug Report, then create rules based on subtype or client custom fields.
                    </p>
                  </div>
                </div>

                {(settings.request_types || []).length > 0 ? (
                  <div className="space-y-4">
                    <div className="flex flex-wrap gap-2">
                      {(settings.request_types || []).map((rt) => (
                        <button
                          key={rt}
                          type="button"
                          onClick={() => setSelectedRequestTypeConfig(rt)}
                          className={`px-3 py-1.5 rounded-lg text-sm border ${selectedRequestTypeConfig === rt ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-gray-700 border-gray-300'}`}
                        >
                          {rt}
                        </button>
                      ))}
                    </div>

                    {selectedRequestTypeConfig && (
                      <div className="border border-gray-200 rounded-lg p-4 bg-gray-50 space-y-4">
                        <div className="flex items-center justify-between">
                          <h5 className="font-medium text-gray-900">{selectedRequestTypeConfig}</h5>
                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              onClick={() => addOptionalSubtypeTemplate(selectedRequestTypeConfig)}
                              className="text-xs px-2 py-1 bg-violet-100 text-violet-700 rounded border border-violet-200"
                            >
                              Add Optional Subtype
                            </button>
                            {showAdvancedFieldKeys && (
                              <>
                                <label className="text-xs text-gray-600">Schema Version</label>
                                <input
                                  type="number"
                                  min="1"
                                  value={settings.request_form_schema_version || 1}
                                  onChange={(e) => handleInputChange('request_form_schema_version', parseInt(e.target.value || '1', 10))}
                                  className="w-20 px-2 py-1 text-sm border border-gray-300 rounded"
                                />
                              </>
                            )}
                          </div>
                        </div>

                        <div className="bg-white border border-gray-200 rounded p-3">
                          <div className="flex items-center justify-between mb-2">
                            <label className="block text-xs font-medium text-gray-700">Optional Instructions (shown to client for this request type)</label>
                            <button type="button" onClick={() => setBuilderCollapsed(prev => ({ ...prev, instructions: !prev.instructions }))} className="text-xs text-gray-600 flex items-center gap-1">
                              {builderCollapsed.instructions ? <ChevronDown className="w-3 h-3" /> : <ChevronUp className="w-3 h-3" />}
                            </button>
                          </div>
                          {!builderCollapsed.instructions && (
                            <textarea
                              value={getRequestTypeConfig(selectedRequestTypeConfig).instructions || ''}
                              onChange={(e) => updateRequestTypeConfig(selectedRequestTypeConfig, { ...getRequestTypeConfig(selectedRequestTypeConfig), instructions: e.target.value })}
                              rows={2}
                              className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                              placeholder="Example: Please include page URL, desired deadline, and any reference examples."
                            />
                          )}
                        </div>

                        <div>
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <p className="text-sm font-medium text-gray-800">Base Fields</p>
                              <button type="button" onClick={() => setBuilderCollapsed(prev => ({ ...prev, baseFields: !prev.baseFields }))} className="text-xs text-gray-600 flex items-center gap-1">
                                {builderCollapsed.baseFields ? <ChevronDown className="w-3 h-3" /> : <ChevronUp className="w-3 h-3" />}
                              </button>
                            </div>
                            <div className="flex items-center gap-2">
                              <button type="button" onClick={() => setShowAdvancedFieldKeys(v => !v)} className="text-xs px-2 py-1 border border-gray-300 rounded text-gray-700">
                                {showAdvancedFieldKeys ? 'Hide Advanced Keys' : 'Show Advanced Keys'}
                              </button>
                              <button type="button" onClick={() => addRequestTypeBaseField(selectedRequestTypeConfig)} className="text-xs px-2 py-1 bg-indigo-600 text-white rounded">Add Base Field</button>
                            </div>
                          </div>
                          {!builderCollapsed.baseFields && <div className="space-y-2">
                            {(getRequestTypeConfig(selectedRequestTypeConfig).base_fields || []).map((f, fi) => (
                              <div key={f.id || f.name || fi} className="grid grid-cols-1 md:grid-cols-6 gap-2 items-center bg-white border border-gray-200 rounded p-2">
                                {showAdvancedFieldKeys && (
                                  <input value={f.id || ''} onChange={(e) => updateRequestTypeBaseField(selectedRequestTypeConfig, f.id || f.name, 'id', e.target.value)} className="px-2 py-1 text-sm border border-gray-300 rounded" placeholder="field_id" />
                                )}
                                <input value={f.label || ''} onChange={(e) => updateRequestTypeBaseField(selectedRequestTypeConfig, f.id || f.name, 'label', e.target.value)} className="px-2 py-1 text-sm border border-gray-300 rounded" placeholder="Question label shown to client" />
                                <select value={((f.type || 'text') === 'select' && (f.options_source || 'static') === 'client_custom_field') ? 'select_client' : (((f.type || 'text') === 'select' && (f.options_source || 'static') === 'reference_table') ? 'select_reference' : (f.type || 'text'))} onChange={(e) => {
                                  const v = e.target.value;
                                  if (v === 'select_client') {
                                    updateRequestTypeBaseField(selectedRequestTypeConfig, f.id || f.name, 'type', 'select');
                                    updateRequestTypeBaseField(selectedRequestTypeConfig, f.id || f.name, 'options_source', 'client_custom_field');
                                  } else if (v === 'select_reference') {
                                    updateRequestTypeBaseField(selectedRequestTypeConfig, f.id || f.name, 'type', 'select');
                                    updateRequestTypeBaseField(selectedRequestTypeConfig, f.id || f.name, 'options_source', 'reference_table');
                                  } else {
                                    updateRequestTypeBaseField(selectedRequestTypeConfig, f.id || f.name, 'type', v);
                                    if (v !== 'select') {
                                      updateRequestTypeBaseField(selectedRequestTypeConfig, f.id || f.name, 'options_source', 'static');
                                    }
                                  }
                                }} className="px-2 py-1 text-sm border border-gray-300 rounded">
                                  <option value="text">Text</option><option value="textarea">Textarea</option><option value="select">Select (manual options)</option><option value="select_client">Select (from client custom field)</option><option value="select_reference">Select (from data table)</option><option value="number">Number</option><option value="email">Email</option><option value="date">Date</option>
                                </select>
                                {((f.type || 'text') === 'select') ? (
                                  <select value={f.options_source || 'static'} onChange={(e) => updateRequestTypeBaseField(selectedRequestTypeConfig, f.id || f.name, 'options_source', e.target.value)} className="px-2 py-1 text-sm border border-gray-300 rounded">
                                    <option value="static">Static options</option>
                                    <option value="client_custom_field">Client custom field</option>
                                    <option value="reference_table">Data table</option>
                                  </select>
                                ) : (
                                  <input value={f.placeholder || ''} onChange={(e) => updateRequestTypeBaseField(selectedRequestTypeConfig, f.id || f.name, 'placeholder', e.target.value)} className="px-2 py-1 text-sm border border-gray-300 rounded" placeholder="Placeholder/help text (optional)" />
                                )}
                                {((f.type || 'text') === 'select') ? (
                                  ((f.options_source || 'static') === 'client_custom_field') ? (
                                    <select value={f.options_custom_field_id || f.options_custom_field_name || ''} onChange={(e) => updateRequestTypeBaseField(selectedRequestTypeConfig, f.id || f.name, 'options_custom_field_id', e.target.value)} className="px-2 py-1 text-sm border border-gray-300 rounded">
                                      <option value="">Select client custom field...</option>
                                      {(clientFieldDefinitions || []).map(def => (
                                        <option key={def.id} value={def.name || def.id}>{def.label || def.name}</option>
                                      ))}
                                    </select>
                                  ) : ((f.options_source || 'static') === 'reference_table') ? (
                                    <select value={f.options_reference_table_id || f.options_reference_table_name || ''} onChange={(e) => updateRequestTypeBaseField(selectedRequestTypeConfig, f.id || f.name, 'options_reference_table_id', e.target.value)} className="px-2 py-1 text-sm border border-gray-300 rounded">
                                      <option value="">Select data table...</option>
                                      {(referenceTables || []).map(rt => (
                                        <option key={rt.id} value={rt.name || rt.id}>{rt.name || rt.id}</option>
                                      ))}
                                    </select>
                                  ) : (
                                    <div className="flex items-center gap-1 flex-wrap">
                                      {(f.options || []).map((opt, oi) => (
                                        <span key={`${f.id || f.name}-opt-${oi}`} className="inline-flex items-center gap-1 px-2 py-0.5 text-xs bg-gray-100 border border-gray-300 rounded">
                                          {opt}
                                          <button type="button" className="text-red-600" onClick={() => removeBaseFieldOption(selectedRequestTypeConfig, f.id || f.name, oi)}>×</button>
                                        </span>
                                      ))}
                                      <button type="button" onClick={() => addBaseFieldOption(selectedRequestTypeConfig, f.id || f.name)} className="text-xs px-2 py-1 border border-dashed border-gray-400 rounded text-gray-700">+ Option</button>
                                    </div>
                                  )
                                ) : <div />}
                                <div className="flex items-center justify-between gap-2">
                                  <label className="text-xs"><input type="checkbox" checked={!!f.required} onChange={(e) => updateRequestTypeBaseField(selectedRequestTypeConfig, f.id || f.name, 'required', e.target.checked)} className="mr-1" />Required</label>
                                  <div className="flex items-center gap-2">
                                    <button type="button" onClick={() => moveRequestTypeBaseField(selectedRequestTypeConfig, fi, 'up')} className="text-gray-600 text-xs">↑</button>
                                    <button type="button" onClick={() => moveRequestTypeBaseField(selectedRequestTypeConfig, fi, 'down')} className="text-gray-600 text-xs">↓</button>
                                    <button type="button" onClick={() => removeRequestTypeBaseField(selectedRequestTypeConfig, f.id || f.name)} className="text-red-600 text-xs">Remove</button>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>}
                        </div>

                        <div>
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <p className="text-sm font-medium text-gray-800">Conditional Rules (Level 1)</p>
                              <button type="button" onClick={() => setBuilderCollapsed(prev => ({ ...prev, conditionalRules: !prev.conditionalRules }))} className="text-xs text-gray-600 flex items-center gap-1">
                                {builderCollapsed.conditionalRules ? <ChevronDown className="w-3 h-3" /> : <ChevronUp className="w-3 h-3" />}
                              </button>
                            </div>
                            <button type="button" onClick={() => addRequestTypeRule(selectedRequestTypeConfig)} className="text-xs px-2 py-1 bg-purple-600 text-white rounded">Add Rule</button>
                          </div>
                          {!builderCollapsed.conditionalRules && <div className="space-y-3">
                            {(getRequestTypeConfig(selectedRequestTypeConfig).conditional_rules || []).map((rule, ri) => (
                              <div key={rule.id || ri} className="bg-white border border-gray-200 rounded p-3 space-y-2">
                                <div className="grid grid-cols-1 md:grid-cols-5 gap-2">
                                  <select value={rule.when?.source || 'form'} onChange={(e) => updateRequestTypeRule(selectedRequestTypeConfig, rule.id, (r) => ({ ...r, when: { ...(r.when || {}), source: e.target.value, field: '' } }))} className="px-2 py-1 text-sm border border-gray-300 rounded">
                                    <option value="form">Rule source: form fields</option>
                                    <option value="client_custom">Rule source: client variables</option>
                                  </select>
                                  <select value={rule.when?.field || ''} onChange={(e) => updateRequestTypeRule(selectedRequestTypeConfig, rule.id, (r) => ({ ...r, when: { ...(r.when || {}), field: e.target.value } }))} className="px-2 py-1 text-sm border border-gray-300 rounded">
                                    <option value="">when field...</option>
                                    {getConditionFieldCandidates(selectedRequestTypeConfig)
                                      .filter(f => (rule.when?.source || 'form') === 'client_custom' ? f.source === 'client_custom' : f.source !== 'client_custom')
                                      .map((f) => (
                                        <option key={f.id} value={f.id}>{f.label}</option>
                                      ))}
                                  </select>
                                  <select value={rule.when?.op || 'equals'} onChange={(e) => updateRequestTypeRule(selectedRequestTypeConfig, rule.id, (r) => ({ ...r, when: { ...(r.when || {}), op: e.target.value } }))} className="px-2 py-1 text-sm border border-gray-300 rounded">
                                    <option value="equals">equals</option><option value="not_equals">not_equals</option><option value="in">in</option>
                                  </select>
                                  {(() => {
                                    const selectedField = getConditionFieldCandidates(selectedRequestTypeConfig).find(f => f.id === (rule.when?.field || ''));
                                    const valueOptions = selectedField?.options || [];
                                    if (valueOptions.length > 0) {
                                      return (
                                        <select value={rule.when?.value || ''} onChange={(e) => updateRequestTypeRule(selectedRequestTypeConfig, rule.id, (r) => ({ ...r, when: { ...(r.when || {}), value: e.target.value } }))} className="px-2 py-1 text-sm border border-gray-300 rounded">
                                          <option value="">value...</option>
                                          {valueOptions.map((opt, oi) => <option key={`${selectedField?.id || 'field'}-${oi}`} value={opt}>{opt}</option>)}
                                        </select>
                                      );
                                    }
                                    return <input value={rule.when?.value || ''} onChange={(e) => updateRequestTypeRule(selectedRequestTypeConfig, rule.id, (r) => ({ ...r, when: { ...(r.when || {}), value: e.target.value } }))} className="px-2 py-1 text-sm border border-gray-300 rounded" placeholder="value" />;
                                  })()}
                                  <div className="flex items-center gap-2">
                                    <button type="button" onClick={() => moveRequestTypeRule(selectedRequestTypeConfig, ri, 'up')} className="text-gray-600 text-xs">↑</button>
                                    <button type="button" onClick={() => moveRequestTypeRule(selectedRequestTypeConfig, ri, 'down')} className="text-gray-600 text-xs">↓</button>
                                    <button type="button" onClick={() => removeRequestTypeRule(selectedRequestTypeConfig, rule.id)} className="text-red-600 text-xs">Remove Rule</button>
                                  </div>
                                </div>

                                <div>
                                  <label className="block text-xs font-medium text-gray-600 mb-1">Optional instruction for this rule/subtype</label>
                                  <textarea
                                    value={rule.instructions || ''}
                                    onChange={(e) => updateRequestTypeRule(selectedRequestTypeConfig, rule.id, (r) => ({ ...r, instructions: e.target.value }))}
                                    rows={2}
                                    className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
                                    placeholder="Shown only when this rule matches. Example: Include page URL and desired publish date."
                                  />
                                </div>

                                <div>
                                  <div className="flex items-center justify-between mb-1">
                                    <span className="text-xs font-medium text-gray-700">Show Fields</span>
                                    <button type="button" onClick={() => addRuleShowField(selectedRequestTypeConfig, rule.id)} className="text-xs px-2 py-0.5 bg-indigo-100 text-indigo-700 rounded">Add Field</button>
                                  </div>
                                  <div className="space-y-2">
                                    {(rule.show_fields || []).map((f, i) => (
                                      <div key={f.id || i} className="grid grid-cols-1 md:grid-cols-6 gap-2 items-center">
                                        {showAdvancedFieldKeys && (
                                          <input value={f.id || ''} onChange={(e) => updateRequestTypeRule(selectedRequestTypeConfig, rule.id, (r) => ({ ...r, show_fields: (r.show_fields || []).map((sf, sfi) => sfi === i ? { ...sf, id: e.target.value } : sf) }))} className="px-2 py-1 text-xs border border-gray-300 rounded" placeholder="field_id" />
                                        )}
                                        <input value={f.label || ''} onChange={(e) => updateRequestTypeRule(selectedRequestTypeConfig, rule.id, (r) => ({ ...r, show_fields: (r.show_fields || []).map((sf, sfi) => sfi === i ? { ...sf, label: e.target.value } : sf) }))} className="px-2 py-1 text-xs border border-gray-300 rounded" placeholder="Question label shown to client" />
                                        <select value={((f.type || 'text') === 'select' && (f.options_source || 'static') === 'client_custom_field') ? 'select_client' : (((f.type || 'text') === 'select' && (f.options_source || 'static') === 'reference_table') ? 'select_reference' : (f.type || 'text'))} onChange={(e) => {
                                          const v = e.target.value;
                                          if (v === 'select_client') {
                                            updateRequestTypeRule(selectedRequestTypeConfig, rule.id, (r) => ({ ...r, show_fields: (r.show_fields || []).map((sf, sfi) => sfi === i ? { ...sf, type: 'select', options_source: 'client_custom_field' } : sf) }));
                                          } else if (v === 'select_reference') {
                                            updateRequestTypeRule(selectedRequestTypeConfig, rule.id, (r) => ({ ...r, show_fields: (r.show_fields || []).map((sf, sfi) => sfi === i ? { ...sf, type: 'select', options_source: 'reference_table' } : sf) }));
                                          } else {
                                            updateRequestTypeRule(selectedRequestTypeConfig, rule.id, (r) => ({ ...r, show_fields: (r.show_fields || []).map((sf, sfi) => sfi === i ? { ...sf, type: v, options_source: v === 'select' ? (sf.options_source || 'static') : 'static' } : sf) }));
                                          }
                                        }} className="px-2 py-1 text-xs border border-gray-300 rounded">
                                          <option value="text">Text</option><option value="textarea">Textarea</option><option value="select">Select (manual options)</option><option value="select_client">Select (from client custom field)</option><option value="select_reference">Select (from data table)</option><option value="number">Number</option><option value="email">Email</option><option value="date">Date</option>
                                        </select>
                                        {((f.type || 'text') === 'select') ? (
                                          <select value={f.options_source || 'static'} onChange={(e) => updateRequestTypeRule(selectedRequestTypeConfig, rule.id, (r) => ({ ...r, show_fields: (r.show_fields || []).map((sf, sfi) => sfi === i ? { ...sf, options_source: e.target.value } : sf) }))} className="px-2 py-1 text-xs border border-gray-300 rounded">
                                            <option value="static">Static options</option>
                                            <option value="client_custom_field">Client custom field</option>
                                            <option value="reference_table">Data table</option>
                                          </select>
                                        ) : (
                                          <input value={f.placeholder || ''} onChange={(e) => updateRequestTypeRule(selectedRequestTypeConfig, rule.id, (r) => ({ ...r, show_fields: (r.show_fields || []).map((sf, sfi) => sfi === i ? { ...sf, placeholder: e.target.value } : sf) }))} className="px-2 py-1 text-xs border border-gray-300 rounded" placeholder="Placeholder/help text (optional)" />
                                        )}
                                        {((f.type || 'text') === 'select') ? (
                                          ((f.options_source || 'static') === 'client_custom_field') ? (
                                            <select value={f.options_custom_field_id || f.options_custom_field_name || ''} onChange={(e) => updateRequestTypeRule(selectedRequestTypeConfig, rule.id, (r) => ({ ...r, show_fields: (r.show_fields || []).map((sf, sfi) => sfi === i ? { ...sf, options_custom_field_id: e.target.value } : sf) }))} className="px-2 py-1 text-xs border border-gray-300 rounded">
                                              <option value="">Select client custom field...</option>
                                              {(clientFieldDefinitions || []).map(def => (
                                                <option key={def.id} value={def.name || def.id}>{def.label || def.name}</option>
                                              ))}
                                            </select>
                                          ) : ((f.options_source || 'static') === 'reference_table') ? (
                                            <select value={f.options_reference_table_id || f.options_reference_table_name || ''} onChange={(e) => updateRequestTypeRule(selectedRequestTypeConfig, rule.id, (r) => ({ ...r, show_fields: (r.show_fields || []).map((sf, sfi) => sfi === i ? { ...sf, options_reference_table_id: e.target.value } : sf) }))} className="px-2 py-1 text-xs border border-gray-300 rounded">
                                              <option value="">Select data table...</option>
                                              {(referenceTables || []).map(rt => (
                                                <option key={rt.id} value={rt.name || rt.id}>{rt.name || rt.id}</option>
                                              ))}
                                            </select>
                                          ) : (
                                            <div className="flex items-center gap-1 flex-wrap">
                                              {(f.options || []).map((opt, oi) => (
                                                <span key={`${f.id || i}-opt-${oi}`} className="inline-flex items-center gap-1 px-2 py-0.5 text-xs bg-gray-100 border border-gray-300 rounded">
                                                  {opt}
                                                  <button type="button" className="text-red-600" onClick={() => removeRuleFieldOption(selectedRequestTypeConfig, rule.id, i, oi)}>×</button>
                                                </span>
                                              ))}
                                              <button type="button" onClick={() => addRuleFieldOption(selectedRequestTypeConfig, rule.id, i)} className="text-xs px-2 py-1 border border-dashed border-gray-400 rounded text-gray-700">+ Option</button>
                                            </div>
                                          )
                                        ) : <div />}
                                        <div className="flex items-center gap-2">
                                          <label className="text-xs"><input type="checkbox" checked={!!f.required} onChange={(e) => updateRequestTypeRule(selectedRequestTypeConfig, rule.id, (r) => ({ ...r, show_fields: (r.show_fields || []).map((sf, sfi) => sfi === i ? { ...sf, required: e.target.checked } : sf) }))} className="mr-1" />Required</label>
                                          <button type="button" onClick={() => moveRuleShowField(selectedRequestTypeConfig, rule.id, i, 'up')} className="text-gray-600 text-xs">↑</button>
                                          <button type="button" onClick={() => moveRuleShowField(selectedRequestTypeConfig, rule.id, i, 'down')} className="text-gray-600 text-xs">↓</button>
                                          <button type="button" onClick={() => removeRuleShowField(selectedRequestTypeConfig, rule.id, i)} className="text-red-600 text-xs">Remove Field</button>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500">Add request types first to configure conditional forms.</p>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Request Type to Template Mapping */}
        {settings.enable_request_form && (
          <div className="space-y-6">
            <div className="border-t border-gray-200 pt-6">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                    <Layers className="w-5 h-5 mr-2 text-purple-600" />
                    Request Type Mapping
                  </h3>
                  <p className="text-sm text-gray-600 mt-1">
                    Map request types to a project category and/or task template. Project placement matches an existing project by category (no new project is created). Unmapped types will create custom tasks in the client's General project.
                  </p>
                </div>
              </div>

              <div className="space-y-4">
                {settings.request_types && settings.request_types.length > 0 && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-sm text-green-800">
                    <p><strong>✓ {settings.request_types.length} Request Type(s) found!</strong> Map each type below to automatically create projects or tasks from templates.</p>
                  </div>
                )}
                
                {(settings.request_types || []).map((requestType, index) => {
                    const mapping = settings.request_type_mappings?.[requestType] || {};
                    const projectCategory = mapping.project_category || mapping.category || '';
                    const taskTemplateId = mapping.task_template_id || '';

                    return (
                      <div key={index} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                        <div className="flex items-center justify-between mb-3">
                          <span className="font-medium text-gray-900">
                            {requestType}
                          </span>
                        </div>

                        <div className="space-y-4">
                          {/* Project Category Selection */}
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Project Category (Optional)
                            </label>
                            <select
                              value={projectCategory}
                              onChange={(e) => {
                                const newMappings = { ...settings.request_type_mappings };
                                if (!newMappings[requestType]) {
                                  newMappings[requestType] = {};
                                }
                                if (e.target.value === '') {
                                  delete newMappings[requestType].project_category;
                                  delete newMappings[requestType].category;
                                } else {
                                  newMappings[requestType].project_category = e.target.value;
                                }
                                // Clean up if no mappings left
                                if (Object.keys(newMappings[requestType]).length === 0) {
                                  delete newMappings[requestType];
                                }
                                handleInputChange('request_type_mappings', newMappings);
                              }}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                            >
                              <option value="">None (use General project)</option>
                              {projectCategories.map(category => (
                                <option key={category} value={category}>
                                  {category}
                                </option>
                              ))}
                            </select>
                            <p className="text-xs text-gray-500 mt-1">
                              Routes to an existing project that matches this category
                            </p>
                          </div>

                          {/* Task Template Selection */}
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Task Template (Optional)
                            </label>
                            <select
                              value={taskTemplateId}
                              onChange={(e) => {
                                const newMappings = { ...settings.request_type_mappings };
                                if (!newMappings[requestType]) {
                                  newMappings[requestType] = {};
                                }
                                if (e.target.value === '') {
                                  delete newMappings[requestType].task_template_id;
                                } else {
                                  newMappings[requestType].task_template_id = e.target.value;
                                }
                                // Clean up if no mappings left
                                if (Object.keys(newMappings[requestType]).length === 0) {
                                  delete newMappings[requestType];
                                }
                                handleInputChange('request_type_mappings', newMappings);
                              }}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                            >
                              <option value="">None (default values)</option>
                              {taskTemplates.map(template => (
                                <option key={template.id} value={template.id}>
                                  {template.name}
                                </option>
                              ))}
                            </select>
                            <p className="text-xs text-gray-500 mt-1">
                              Applies task settings (priority, category, effort) to the created task
                            </p>
                          </div>

                          {/* Display current mapping */}
                          {(projectCategory || taskTemplateId) && (
                            <div className="bg-blue-50 border border-blue-200 rounded p-2 text-xs">
                              <span className="font-medium text-blue-900">Active:</span>
                              <ul className="mt-1 space-y-1 text-blue-800">
                                {projectCategory && (
                                  <li>• Routes to {projectCategory} project</li>
                                )}
                                {taskTemplateId && (
                                  <li>• Applies task template settings</li>
                                )}
                                {!projectCategory && (
                                  <li>• Uses General project (default)</li>
                                )}
                              </ul>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}

                {(!settings.request_types || settings.request_types.length === 0) && (
                  <div className="text-center py-8 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <div className="flex flex-col items-center space-y-3">
                      <Info className="w-8 h-8 text-yellow-600" />
                      <div>
                        <p className="text-gray-700 font-medium">No Request Types Defined</p>
                        <p className="text-sm text-gray-600 mt-2">Add request types above in the "Request Types" section,</p>
                        <p className="text-sm text-gray-600">then map each one to a template here.</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className="mt-4 bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-start space-x-2">
                  <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-blue-800">
                    <p className="font-medium mb-1">How Template Mapping Works:</p>
                    <ul className="list-disc list-inside space-y-1">
                      <li><strong>Project Category Only:</strong> Routes task to an existing project with that category</li>
                      <li><strong>Task Template Only:</strong> Creates a task in General project using template settings (priority, category, effort)</li>
                      <li><strong>Category + Task Template:</strong> Routes to the matching category project and applies task template settings</li>
                      <li><strong>No Mapping:</strong> Creates a custom task in General project with basic request details</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex items-center justify-between pt-6 border-t border-gray-200 mt-8">
          <button
            onClick={resetToDefaults}
            className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
          >
            Reset to Defaults
          </button>
          
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center space-x-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
          >
            {saving ? (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
            ) : (
              <Save className="w-4 h-4" />
            )}
            <span>{saving ? 'Saving...' : 'Save Changes'}</span>
          </button>
        </div>
      </div>
    </>
  );
};

export default ClientPortalSettings;
